import React, { useCallback, useState } from 'react';
import { View, Text, StyleSheet, TouchableOpacity, ActivityIndicator } from 'react-native';
import { router, usePathname, type Href } from 'expo-router';
import type { LucideIcon } from 'lucide-react-native';
import { Camera, Home, Newspaper, ChevronRight } from 'lucide-react-native';
import { useTheme, SPACING, BORDER_RADIUS, TYPOGRAPHY } from '../../theme';
import { useUIStore } from '../../store/uiStore';
import { AdaptiveSheet } from '../common/AdaptiveSheet';
import { useEnsureScout } from '@hooks/useEnsureScout';
import { useObservationGuidance } from '@hooks/useObservationGuidance';
import { useDesktopShell } from '@hooks/useDesktopShell';
import { useFieldMode } from '@hooks/useFieldMode';
import { useWebHover } from '@hooks/useWebHover';
import { isMapRoute } from '../../utils/sidebarVariant';
import { DESKTOP_COMPOSER_DOCK_ENABLED } from '../../constants/composer';

/**
 * The three "Create" actions (new post, share news, add a property) and
 * their handlers, shared by the phone/tablet sheet below and the desktop
 * rail's popover (SidebarNav.tsx) so the two surfaces can't drift apart —
 * one list, one set of handlers, two containers.
 *
 * `onDismiss` is called at the exact point the standalone sheet used to
 * call `close()`: immediately for "Add my property" (the single-active-sheet
 * contract closed the create sheet for us there, so calling it explicitly
 * here is just making that implicit close explicit for the popover too),
 * and after the `ensureScoutThen` auth round-trip for the two composer
 * actions — so the popover gets the same brief spinner-then-navigate
 * behavior the sheet always had, not an abrupt disappearance.
 */
export interface CreateActionMenuItemsProps {
  onDismiss: () => void;
  /** 'sheet' (default): the phone/tablet bottom-sheet rows this component
   *  was designed for — hairline dividers, full-sentence subtitles,
   *  chevrons. 'popover': the desktop rail's compact menu (SidebarNav.tsx)
   *  — narrower rows with a rounded hover fill and short subtitles, no
   *  chevrons; see docs/plans/panel-system-review-notes.md Note 9. */
  variant?: 'sheet' | 'popover';
}

/** One row's icon + label + subtitle for both variants — the popover's
 *  subtitle is a shorter rewrite of the sheet's, not a truncation, so both
 *  read as complete sentences/phrases rather than one being a cut-off of
 *  the other. */
interface ActionItemConfig {
  key: string;
  icon: LucideIcon;
  label: string;
  sheetSubtitle: string;
  popoverSubtitle: string;
  onPress: () => void;
  accessibilityLabel: string;
  busy?: boolean;
}

/** A single row in the desktop popover: rounded hover fill instead of the
 *  sheet's hairline divider, no chevron. Web-only affordance (useWebHover
 *  is inert on native), but this only ever renders inside the desktop
 *  shell's Popover. */
function PopoverActionRow({
  icon: Icon,
  label,
  subtitle,
  onPress,
  accessibilityLabel,
  busy,
}: Omit<ActionItemConfig, 'key' | 'sheetSubtitle' | 'popoverSubtitle'> & { subtitle: string }) {
  const { colors } = useTheme();
  const { hovered, hoverProps } = useWebHover();

  return (
    <TouchableOpacity
      {...hoverProps}
      style={[
        styles.popoverOption,
        { borderRadius: BORDER_RADIUS.sm },
        hovered && { backgroundColor: colors.hoverSurface },
        busy && styles.busy,
      ]}
      onPress={onPress}
      disabled={busy}
      accessibilityRole="button"
      accessibilityState={{ busy: Boolean(busy) }}
      accessibilityLabel={accessibilityLabel}>
      {busy ? (
        <ActivityIndicator size="small" color={colors.text} style={styles.icon} />
      ) : (
        <Icon size={18} color={colors.text} style={styles.icon} />
      )}
      <View style={styles.optionText}>
        <Text style={[styles.optionLabel, { color: colors.text }]}>{label}</Text>
        <Text style={[styles.popoverOptionBlurb, { color: colors.textSecondary }]}>{subtitle}</Text>
      </View>
    </TouchableOpacity>
  );
}

export function CreateActionMenuItems({
  onDismiss,
  variant = 'sheet',
}: CreateActionMenuItemsProps) {
  const { colors } = useTheme();
  const openSheet = useUIStore((s) => s.openSheet);
  const { ensureScoutThen } = useEnsureScout();
  const { guideThen } = useObservationGuidance();
  const [postBusy, setPostBusy] = useState(false);
  // Desktop map route only (panel-system.md Phase 3A): "New post" opens the
  // composer in a right dock instead of routing to /post/create — see
  // handleNewPost below and ComposerDockPanel.web.tsx.
  const isDesktopShell = useDesktopShell();
  const onMapRoute = isMapRoute(usePathname());
  const fieldMode = useFieldMode();

  const composeThen = useCallback(
    (path: Href, withGuidance: boolean) => {
      if (postBusy) return;
      setPostBusy(true);
      // Lowest-friction "post an observation": if there's no user, silently
      // create an anonymous Fire Scout in the background and jump straight
      // into the composer. Keeps this menu open with a spinner during the
      // brief auth round-trip so the user knows the tap registered.
      // Tab-bar visibility (sheet mode) is handed off via the sheet's
      // onDismiss handler + the layout's route-based hide on /post/create —
      // calling showTabBar here would briefly expose the bar between sheets.
      ensureScoutThen(() => {
        onDismiss();
        // guideThen runs only once ensureScoutThen's callback actually
        // fires — a blocked/gated user (signed out with ANON_WRITES_ENABLED
        // off, or an anon user past the upgrade wall) never reaches here,
        // so the auth gate still wins over the guidance sheet.
        if (withGuidance) guideThen(path);
        else router.push(path);
      }).finally(() => {
        // Either we navigated (router.push/guideThen fired in callback) or
        // ensureScoutThen opened the auth-gate fallback. In both cases
        // we want the spinner gone and this menu dismissable again.
        setPostBusy(false);
      });
    },
    [postBusy, ensureScoutThen, onDismiss, guideThen]
  );

  const handleNewPost = useCallback(() => {
    // Desktop map route, flag on: dock the composer instead of routing.
    // Skips the observation-guidance gate (unlike the phone/route path
    // below) — that sheet's Continue always does a router.push, which
    // would defeat opening a dock for a first-time-on-device user.
    if (DESKTOP_COMPOSER_DOCK_ENABLED && isDesktopShell && onMapRoute) {
      if (postBusy) return;
      setPostBusy(true);
      ensureScoutThen(() => {
        onDismiss();
        useUIStore.getState().openComposerDock();
      }).finally(() => setPostBusy(false));
      return;
    }
    composeThen('/post/create', true);
  }, [composeThen, isDesktopShell, onMapRoute, postBusy, ensureScoutThen, onDismiss]);
  // News is text-first (no photo required) — same composer, preset to the
  // news category, matching the guidance's "News" path. Not gated by the
  // observation guidance sheet — that's about what to look for in the
  // field, not news writing.
  const handleShareNews = useCallback(
    () => composeThen('/post/create?category=news', false),
    [composeThen]
  );

  const handleAddProperty = useCallback(() => {
    // No proactive showTabBar (sheet mode) — the ref-counted hide stacks
    // cleanly when AddPropertySheet's hideTabBar fires before this sheet's
    // onDismiss releases its hold.
    // AddPropertySheet handles the anonymous-to-registered upgrade flow
    // itself, so we don't gate auth here — anonymous users just see the
    // upgrade step before submit.
    onDismiss();
    openSheet('addProperty');
  }, [onDismiss, openSheet]);

  const allItems: ActionItemConfig[] = [
    {
      key: 'new-post',
      icon: Camera,
      label: 'New post',
      sheetSubtitle: 'Share an observation, progress photo, or opportunity',
      popoverSubtitle: 'Photo or note',
      onPress: handleNewPost,
      accessibilityLabel: 'Create a new post',
      busy: postBusy,
    },
    {
      key: 'share-news',
      icon: Newspaper,
      label: 'Share news',
      sheetSubtitle: 'Tell your community what is happening. Text is enough',
      popoverSubtitle: 'Text update, no photo',
      onPress: handleShareNews,
      accessibilityLabel: 'Share news',
      busy: postBusy,
    },
    {
      key: 'add-property',
      icon: Home,
      label: 'Add my property',
      sheetSubtitle: "Add a building that isn't on the map yet. You'll claim it automatically",
      popoverSubtitle: 'A home not on the map yet',
      onPress: handleAddProperty,
      accessibilityLabel: 'Add a property',
    },
  ];

  // Field mode (phone website): no news posting.
  const items = fieldMode ? allItems.filter((item) => item.key !== 'share-news') : allItems;

  if (variant === 'popover') {
    return (
      <View style={styles.popoverOptions}>
        {items.map((item) => (
          <PopoverActionRow
            key={item.key}
            icon={item.icon}
            label={item.label}
            subtitle={item.popoverSubtitle}
            onPress={item.onPress}
            accessibilityLabel={item.accessibilityLabel}
            busy={item.busy}
          />
        ))}
      </View>
    );
  }

  return (
    <View style={[styles.options, { borderTopColor: colors.border }]}>
      {items.map((item) => (
        <TouchableOpacity
          key={item.key}
          style={[styles.option, { borderBottomColor: colors.border }, item.busy && styles.busy]}
          onPress={item.onPress}
          disabled={item.busy}
          accessibilityRole="button"
          accessibilityState={{ busy: Boolean(item.busy) }}
          accessibilityLabel={item.accessibilityLabel}>
          {item.busy ? (
            <ActivityIndicator size="small" color={colors.text} style={styles.icon} />
          ) : (
            <item.icon size={18} color={colors.text} style={styles.icon} />
          )}
          <View style={styles.optionText}>
            <Text style={[styles.optionLabel, { color: colors.text }]}>{item.label}</Text>
            <Text style={[styles.optionBlurb, { color: colors.textSecondary }]}>
              {item.sheetSubtitle}
            </Text>
          </View>
          <ChevronRight size={16} color={colors.textTertiary} />
        </TouchableOpacity>
      ))}
    </View>
  );
}

/**
 * Action sheet revealed by the "+" tab on phone/tablet (the desktop shell's
 * "+" opens a Popover of CreateActionMenuItems instead — SidebarNav.tsx —
 * and never calls openSheet('createMenu')). Lives in the root layout so any
 * screen can summon it via `useUIStore.getState().openSheet('createMenu')`.
 *
 * Hairline rows, ink icons, left-aligned title — the same grammar as the
 * post-create pickers (see ChecklistItemPickerSection). The boxed options
 * with green circle icon chips were the pre-Design-B treatment.
 */
export function CreateActionSheet() {
  const { colors } = useTheme();
  const open = useUIStore((s) => s.activeSheet === 'createMenu');
  const sheetOpenTick = useUIStore((s) => s.sheetOpenTick);
  const close = useUIStore((s) => s.closeSheet);

  // The dismiss may be from a user tap OR because openSheet() handed
  // off to another sheet — only clear activeSheet if it's still us.
  const handleSheetDismissed = useCallback(() => {
    if (useUIStore.getState().activeSheet === 'createMenu') {
      close();
    }
  }, [close]);

  return (
    <AdaptiveSheet
      isOpen={open}
      tick={sheetOpenTick}
      backdrop="light"
      onRequestClose={close}
      onDismiss={handleSheetDismissed}
      dialogWidth={420}>
      <AdaptiveSheet.View style={styles.content}>
        <Text style={[styles.title, { color: colors.text }]}>Create</Text>
        <CreateActionMenuItems onDismiss={close} />
      </AdaptiveSheet.View>
    </AdaptiveSheet>
  );
}

const styles = StyleSheet.create({
  content: {
    padding: SPACING.lg,
    paddingBottom: SPACING.xxl,
  },
  title: {
    ...TYPOGRAPHY.h4,
    marginBottom: SPACING.md,
  },
  options: {
    borderTopWidth: 1,
  },
  option: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.smd,
    paddingVertical: SPACING.smd,
    borderBottomWidth: 1,
  },
  busy: {
    opacity: 0.7,
  },
  // Fixed box so the spinner and the icon occupy the same width and the
  // labels don't shift when the tap starts.
  icon: {
    width: 18,
    textAlign: 'center',
  },
  optionText: {
    flex: 1,
    gap: 2,
  },
  optionLabel: {
    ...TYPOGRAPHY.label,
  },
  optionBlurb: {
    ...TYPOGRAPHY.bodySmall,
    lineHeight: 16,
  },
  // Desktop popover (SidebarNav's rail "+"): fixed and narrower than the
  // sheet dialog, so it reads as a menu off the button, not a
  // search-results dropdown (panel-system-review-notes.md Note 9). Padding
  // lives on this container, not the row below, which stays borderless so
  // its rounded hover fill can sit flush against the container's padding.
  popoverOptions: {
    width: 280,
    padding: SPACING.sm,
    gap: SPACING.xxs,
  },
  popoverOption: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: SPACING.sm,
    paddingVertical: SPACING.sm,
    paddingHorizontal: SPACING.smd,
  },
  popoverOptionBlurb: {
    ...TYPOGRAPHY.caption,
  },
});
